import { normaliseTrackedPath } from '@/lib/analytics/site-paths'
import { getCookie } from '@/lib/utils/cookie-client'

/** Cookie written by the consent banner. Shared so tracking honours the choice. */
export const COOKIE_CONSENT_NAME = 'prescia-cookie-consent'

interface SitePageViewInput {
  path: string
  /** First page view of this page load, as opposed to an in-app navigation. */
  entrance: boolean
}

/**
 * Whether the browser has opted out of measurement: an explicit "reject" in the
 * consent banner, Do Not Track, or Global Privacy Control. The tracker sets no
 * cookie or storage, but we honour the signals anyway.
 */
export function isSiteTrackingDisabled(): boolean {
  if (typeof window === 'undefined') return true
  if (getCookie(COOKIE_CONSENT_NAME) === 'rejected') return true
  if (navigator.doNotTrack === '1') return true
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  )
}

/** Sends one anonymous page view to `/api/analytics/collect`. Fire-and-forget. */
export function trackSitePageView({ path, entrance }: SitePageViewInput): void {
  const tracked = normaliseTrackedPath(path)
  if (!tracked || isSiteTrackingDisabled()) return

  const params = new URLSearchParams(window.location.search)
  const body = JSON.stringify({
    path: tracked,
    entrance,
    // Referrer and campaign tags describe how the visitor arrived, so they
    // only apply to the first view of a page load.
    ...(entrance
      ? {
          referrer: document.referrer || undefined,
          utmSource: params.get('utm_source') ?? undefined,
          utmMedium: params.get('utm_medium') ?? undefined,
          utmCampaign: params.get('utm_campaign') ?? undefined,
        }
      : {}),
  })

  if ('sendBeacon' in navigator) {
    const blob = new Blob([body], { type: 'application/json' })
    if (navigator.sendBeacon('/api/analytics/collect', blob)) return
  }

  void fetch('/api/analytics/collect', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Analytics must never surface errors to the user.
  })
}
